/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { indexOf, isEqual } from 'lodash';
import { Dropdown, Loader } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { Avatar } from '../../../components';
import LogoutItem from '../../containers/userMenu/LogoutItem';
import ProfileItem from './ProfileItem';
import { setIsSysadmin, setUserData } from '../../../actions/user';

const GET_USER_QUERY = gql`
    query getUser {
        viewer {
            id
            ... on Administrator {
                firstName
                lastName
                email
                phone
                roles
                emailVerified
            }
            ... on Supplier {
                firstName
                lastName
                email
                phone
                roles
            }
            ... on Master {
                firstName
                lastName
                email
                phone
                roles
            }
        }
    }
`;

const AvatarWrapper = styled.span`
    display: inline-block;
    vertical-align: middle;
    font-weight: 600;
`;

const UserMenu = () => {
    const dispatch = useDispatch();
    const userData = useSelector(({ user }) => user.get('userData'));

    const { loading, data } = useQuery(GET_USER_QUERY, {
        fetchPolicy: 'cache-and-network',
    });
    const user = data && data.viewer;

    const isSysadmin =
        !loading && !!user && indexOf(user.roles, 'SYSADMIN') !== -1;

    // Keep the sysadmin flag in sync with the resolved viewer.
    useEffect(() => {
        dispatch(setIsSysadmin(isSysadmin));
    }, [dispatch, isSysadmin]);

    // Mirror the viewer's profile fields into Redux when they change.
    useEffect(() => {
        if (!user) return;
        const next = {
            firstName: user.firstName,
            lastName: user.lastName,
            phone: user.phone,
            email: user.email,
        };
        if (!isEqual(next, userData)) {
            dispatch(setUserData(next));
        }
    }, [dispatch, user, userData]);

    // Show spinner while loading user details (or before the authenticated
    // viewer has resolved — guards against a null user crashing the render).
    if (loading || !user) {
        return <Loader size="small" active />;
    }

    return (
        <Dropdown
            trigger={
                <AvatarWrapper>
                    {`${user.firstName} ${user.lastName}`}
                    <Avatar
                        size={30}
                        name={`${user.firstName} ${user.lastName}`}
                        spaced="left"
                        round
                    />
                </AvatarWrapper>
            }
            icon={null}
        >
            <Dropdown.Menu>
                <ProfileItem administrator={user} />
                <Dropdown.Item
                    as={Link}
                    to="/salon/invoices"
                    icon="newspaper"
                    text={
                        <FormattedMessage
                            id="pages.administrator.InvoicesPage.title"
                            defaultMessage="Invoices"
                        />
                    }
                />
                <LogoutItem />
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default UserMenu;
